// Billing API types — matches backend schemas/stripe_subscription.go

export type BillingInterval = "month" | "year";

/** Display names for tier identifiers. Single source of truth. */
export const TIER_DISPLAY_NAMES: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  biz: "Business",
  enterprise: "Enterprise",
};

export const getTierDisplayName = (tier: string) => TIER_DISPLAY_NAMES[tier] ?? tier;

export interface TierInfo {
  name: string;
  cuAmount: number; // raw CU count (e.g. 400000)
  aiCredits: number;
  priceUSDCents: number; // cents (e.g. 1900 = $19)
  annualPriceUSDCents: number; // cents, annual
  maxActiveWorkflows: number;
  maxDatabaseTables: number;
  maxUsers: number; // 0 means unlimited
  supportLevel: string; // community, email, priority, dedicated
}

export interface ListTiersResponse {
  code: number;
  message: string;
  data: { tiers: TierInfo[] };
  requestId: string;
}

export interface CreateCheckoutSessionResponse {
  code: number;
  message: string;
  data: {
    clientSecret: string;
  };
  requestId: string;
}

export interface UpgradeStripeSubscriptionResponse {
  code: number;
  message: string;
  data: {
    subscriptionId: string;
    oldTier: string;
    newTier: string;
    interval: string;
  };
  requestId: string;
}

export interface DowngradeStripeSubscriptionResponse {
  code: number;
  message: string;
  data: {
    subscriptionId: string;
    oldTier: string;
    newTier: string;
    effectiveAt: string; // RFC3339
  };
  requestId: string;
}

export interface ReactivateSubscriptionResponse {
  code: number;
  message: string;
  data: {
    subscriptionId: string;
    tier: string;
  };
  requestId: string;
}

export interface BillingPaymentMethod {
  id: string;
  type: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
  billingName?: string;
  billingEmail?: string;
  createdAt?: string;
}

export interface BillingInvoice {
  id: string;
  number?: string;
  status: string;
  currency: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  total: number;
  createdAt?: string;
  dueAt?: string;
  periodStart?: string;
  periodEnd?: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

export interface StripeBillingDetailsData {
  paymentMethods: BillingPaymentMethod[];
  invoices: BillingInvoice[];
}

export interface StripeBillingDetailsResponse {
  code: number;
  message: string;
  data: StripeBillingDetailsData;
  requestId: string;
}

export interface CreateSetupIntentResponse {
  code: number;
  message: string;
  data: {
    clientSecret: string;
  };
  requestId: string;
}

export interface DetachPaymentMethodResponse {
  code: number;
  message: string;
  data: null;
  requestId: string;
}

export interface PreviewUpgradeTierResult {
  amountDueCents: number;
}

export interface PreviewUpgradeData {
  previews: Record<string, PreviewUpgradeTierResult>;
}

export interface PreviewUpgradeResponse {
  code: number;
  message: string;
  data: PreviewUpgradeData;
  requestId: string;
}
